import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { blogRedirectTarget } from "@/lib/blog-redirects";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { Clock, ArrowLeft, Calendar, ChevronDown } from "lucide-react";
import { getPublishedPostBySlug, categoryLabel } from "@/lib/blog.functions";
import { BlogMarkdown } from "@/components/blog/BlogMarkdown";
import { BlogShareButtons } from "@/components/blog/BlogShareButtons";
import { BlogArticleCTA } from "@/components/blog/BlogArticleCTA";
import {
  ArticlePracticeCTA,
  InlineArticlePractice,
} from "@/components/blog/ArticlePracticeCTA";
import { ReadingProgress } from "@/components/blog/ReadingProgress";
import { SourcesList } from "@/components/blog/SourcesList";
import { citationsForPost } from "@/data/citations";
import { TableOfContents } from "@/components/blog/TableOfContents";
import {
  InternalLinkCluster,
  ClusterBreadcrumbNote,
} from "@/components/blog/InternalLinkCluster";
import { BlogCard } from "./blog.index";
import { browserLocale } from "@/lib/locale";

/**
 * Split an article body at the `##` heading closest to the middle, so a single
 * relevant practice can be offered naturally part-way through. Long articles
 * only — short ones keep one uninterrupted read.
 */
function splitBodyForInlinePractice(markdown: string): [string, string | null] {
  const lines = markdown.split("\n");
  if (lines.length < 60) return [markdown, null];
  const headings = lines
    .map((line, i) => (/^##\s+\S/.test(line) ? i : -1))
    .filter((i) => i > 10 && i < lines.length - 10);
  if (headings.length < 3) return [markdown, null];
  const mid = lines.length / 2;
  const at = headings.reduce((best, i) => (Math.abs(i - mid) < Math.abs(best - mid) ? i : best), headings[0]);
  return [lines.slice(0, at).join("\n"), lines.slice(at).join("\n")];
}

const postQuery = (slug: string) => ({
  queryKey: ["blog", "post", slug],
  queryFn: async () => {
    const res = await getPublishedPostBySlug({
      data: typeof window === "undefined" ? { slug } : { slug, lang: browserLocale() },
    });
    if (!res) throw notFound();
    return res;
  },
  staleTime: 60_000,
});

const SITE = "https://recoverytrauma.com";

export const Route = createFileRoute("/blog/$slug")({
  // Consolidated articles keep their old address working: a permanent redirect
  // to the surviving pillar, before anything tries to load the missing post.
  beforeLoad: ({ params }) => {
    const target = blogRedirectTarget(params.slug);
    if (target) {
      throw redirect({
        to: "/blog/$slug",
        params: { slug: target },
        statusCode: 301,
        replace: true,
      });
    }
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(postQuery(params.slug)),
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Article not found — Recovery Trauma™" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const p = loaderData.post;
    const url = `${SITE}/blog/${params.slug}`;
    // Google truncates around 60 characters. Keep the brand suffix when it
    // fits, drop it rather than ship a clipped title in search results.
    const brandSuffix = /\s*[|—-]\s*Recovery Trauma™?\s*$/;
    const raw = p.meta_title ?? `${p.title} — Recovery Trauma™`;
    const title = raw.length > 60 ? raw.replace(brandSuffix, "").trim() : raw;
    const description = p.meta_description ?? p.excerpt;
    const image = p.cover_image_url;


    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
    ];
    if (image) {
      meta.push({ property: "og:image", content: image });
      meta.push({ name: "twitter:image", content: image });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: p.title,
            description: p.excerpt,
            image: image ?? undefined,
            datePublished: p.published_at ?? undefined,
            dateModified: p.updated_at,
            author: { "@type": "Person", name: p.author_name_override ?? "Recovery Trauma™" },
            publisher: {
              "@type": "Organization",
              name: "Recovery Trauma™",
              url: SITE,
            },
            mainEntityOfPage: url,
            url,
            citation: citationsForPost(p.category, p.slug).map((c) => ({
              "@type": "WebPage",
              name: c.title,
              url: c.url,
              publisher: { "@type": "Organization", name: c.publisher },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
              {
                "@type": "ListItem",
                position: 3,
                name: categoryLabel(p.category),
                item: `${SITE}/blog/category/${p.category}`,
              },
              { "@type": "ListItem", position: 4, name: p.title, item: url },
            ],
          }),
        },

        ...(p.faqs && p.faqs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: p.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.question,
                    acceptedAnswer: { "@type": "Answer", text: f.answer },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold">Article unavailable</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {error instanceof Error ? error.message : "Please try again."}
      </p>
      <button onClick={reset} className="mt-4 text-sm text-primary underline">
        Try again
      </button>
    </div>
  ),
  notFoundComponent: ArticleNotFound,
  component: ArticlePage,
});

function ArticleNotFound() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-20 text-center">
      <h1 className="text-3xl font-semibold">Article not found</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        The article you're looking for doesn't exist or was moved.
      </p>
      <Link to="/blog" className="mt-6 inline-flex items-center gap-1 text-sm text-primary">
        <ArrowLeft className="h-4 w-4" /> Back to the blog
      </Link>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function ArticlePage() {
  const { post, related } = useSuspenseQuery(postQuery(Route.useParams().slug)).data;
  const url = `${SITE}/blog/${post.slug}`;
  const articleRef = useRef<HTMLElement>(null);

  return (
    <>
      <ReadingProgress targetRef={articleRef} />

      <article
        ref={articleRef}
        className="pb-20"
        // Articles already written in this language must not be run through
        // the page translator again.
        {...(post.lang !== "en" ? { "data-no-translate": "" } : {})}
      >
        {/* Hero */}
        <header className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
          <div className="mx-auto max-w-4xl px-4 pt-8 pb-12 sm:pt-12 sm:pb-16">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All articles
            </Link>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Link
                to="/blog/category/$category"
                params={{ category: post.category }}
                className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary transition-colors hover:bg-primary/15"
              >
                {categoryLabel(post.category)}
              </Link>
            </div>

            <h1 className="mt-5 font-display text-3xl font-semibold leading-[1.15] tracking-tight text-foreground sm:text-4xl md:text-5xl">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
                {post.excerpt}
              </p>
            )}

            <ClusterBreadcrumbNote slug={post.slug} />

            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <span
                  aria-hidden
                  className="grid h-8 w-8 place-items-center rounded-full bg-primary/15 font-display text-xs font-semibold text-primary"
                >
                  {(post.author_name_override ?? "Recovery Trauma™")
                    .split(" ")
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join("")}
                </span>
                <span className="font-medium text-foreground">
                  {post.author_name_override ?? "Recovery Trauma™"}
                </span>
              </span>
              {post.published_at && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" /> {formatDate(post.published_at)}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4" /> {post.reading_time_minutes} min read
              </span>
              {post.updated_at && post.updated_at !== post.published_at && (
                <span className="text-xs">Updated {formatDate(post.updated_at)}</span>
              )}
            </div>
          </div>

          {post.cover_image_url && (
            <div className="mx-auto max-w-md px-4 pb-4 sm:max-w-lg">
              <img
                loading="eager"
                decoding="async"
                src={post.cover_image_url}
                alt={post.title}
                className="aspect-[3/2] w-full rounded-2xl object-cover shadow-sm ring-1 ring-border/40"
              />
            </div>
          )}

        </header>

        {/* Body with TOC sidebar */}
        <div className="mx-auto max-w-6xl px-4 pt-10 sm:pt-14">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_16rem] lg:gap-16">
            <div className="min-w-0">
              <div className="mb-8 lg:hidden">
                <TableOfContents markdown={post.body_markdown} />
              </div>

              {(() => {
                const [before, after] = splitBodyForInlinePractice(post.body_markdown);
                return (
                  <>
                    <BlogMarkdown>{before}</BlogMarkdown>
                    {after && (
                      <>
                        <InlineArticlePractice slug={post.slug} category={post.category} />
                        <BlogMarkdown>{after}</BlogMarkdown>
                      </>
                    )}
                  </>
                );
              })()}

              <ArticlePracticeCTA slug={post.slug} category={post.category} />

              <InternalLinkCluster slug={post.slug} />

              <SourcesList sources={citationsForPost(post.category, post.slug)} />

              <p className="mt-6 rounded-xl bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Cite this article:</span>{" "}
                {post.author_name_override ?? "Recovery Trauma™"} ({post.published_at ? new Date(post.published_at).getFullYear() : new Date().getFullYear()}). “{post.title}.” Recovery Trauma™.{" "}
                <a href={url} className="break-all text-primary underline">{url}</a>
              </p>




              <div className="mt-12 border-t border-border pt-6">
                <BlogShareButtons url={url} title={post.title} />
              </div>

              <div className="mt-10">
                <BlogArticleCTA />
              </div>

              {post.faqs && post.faqs.length > 0 && (
                <section className="mt-16">
                  <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
                    Frequently asked questions
                  </h2>
                  <div className="mt-6 space-y-3">
                    {post.faqs.map((f, i) => (
                      <details
                        key={i}
                        className="group rounded-2xl border border-border bg-card/40 px-6 py-5 transition-colors open:bg-card"
                      >
                        <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-foreground marker:hidden">
                          <span>{f.question}</span>
                          <ChevronDown className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                        </summary>
                        <p className="mt-4 leading-relaxed text-muted-foreground">{f.answer}</p>
                      </details>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <aside className="hidden lg:block">
              <TableOfContents markdown={post.body_markdown} />
            </aside>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mx-auto mt-20 max-w-6xl border-t border-border px-4 pt-14">
            <div className="mb-8 flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Keep reading
                </p>
                <h2 className="mt-2 font-display text-2xl font-semibold text-foreground sm:text-3xl">
                  Related articles
                </h2>
              </div>
              <Link
                to="/blog"
                className="hidden text-sm font-medium text-muted-foreground hover:text-primary sm:inline-flex"
              >
                All articles →
              </Link>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.slice(0, 3).map((r) => (
                <BlogCard key={r.id} post={r} />
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}
